import { useState } from 'react';
import { Sun, Moon, Check, X, Eye, EyeOff } from 'lucide-react';
import {
  login,
  completeNewPassword,
  associateTotp,
  verifyTotpSetup,
  completeTotpChallenge,
  isMfaDemoEnabled,
  completeMfaDemoLogin,
  completeLocalPasswordRotation,
  disableRestrictedSessionRefresh,
  logout as cognitoLogout,
} from '../services/auth';
import {
  MFA_DEMO_EMAIL,
  MFA_DEMO_PASSWORD,
  startMfaDemo,
  rememberMfaDemo,
  verifyMfaDemoCode,
} from '../services/mfaDemo';
import { changeExpiredPassword } from '../services/api';
import config from '../config';
import { useAuth } from '../contexts/AuthContext';
import { isDarkTheme, toggleTheme } from '../theme';
import translations from '../i18n/translations';
import TotpQrCode from './TotpQrCode';

function meetsNewPasswordPolicy(value) {
  return value.length >= 8 && /[A-Z]/.test(value) && /[a-z]/.test(value) && /\d/.test(value);
}

// リアルタイムフィードバック用のコンポーネント
function RequirementItem({ met, text, isTyping }) {
  // 条件を満たしていれば緑、入力中で未達なら赤、未入力ならグレー
  const colorClass = met
    ? 'text-green-600 dark:text-green-400'
    : isTyping
    ? 'text-red-500 dark:text-red-400'
    : 'text-light-muted dark:text-secondary';

  return (
    <li className={`flex items-center gap-1.5 transition-colors duration-200 ${colorClass}`}>
      {met ? (
        <Check size={14} />
      ) : isTyping ? (
        <X size={14} />
      ) : (
        <span className="w-3.5 text-center text-lg leading-none opacity-70">•</span>
      )}
      <span>{text}</span>
    </li>
  );
}

export default function ChatLoginPage({ languagePath = 'jp', onChangeLanguage }) {
  const { login: authLogin } = useAuth();
  const t = translations[languagePath === 'en' ? 'EN' : '日本語'].login;
  const isMfaDemo = isMfaDemoEnabled();
  const [isDark, setIsDark] = useState(() => isDarkTheme());
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // New-password challenge state
  const [needsNewPassword, setNeedsNewPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [cognitoUser, setCognitoUser] = useState(null);
  const [passwordRotation, setPasswordRotation] = useState(null);
  const [mfaChallenge, setMfaChallenge] = useState(null);
  const [totpCode, setTotpCode] = useState('');

  const handleToggleTheme = () => {
    toggleTheme();
    setIsDark(isDarkTheme());
  };

  // パスワード条件の動的判定
  const hasMinLength = newPassword.length >= 8;
  const hasUpperCase = /[A-Z]/.test(newPassword);
  const hasLowerCase = /[a-z]/.test(newPassword);
  const hasNumber = /\d/.test(newPassword);
  const isTyping = newPassword.length > 0;

  const canConfirmPassword = meetsNewPasswordPolicy(newPassword);
  const canSubmitLogin = username.trim().length > 0 && password.trim().length > 0;
  const canSubmitNewPassword =
    canConfirmPassword &&
    confirmPassword.length > 0 &&
    (!passwordRotation || password.trim().length > 0);
  const isConfirmPasswordDisabled = !canConfirmPassword;
  const totpSetupUri = mfaChallenge?.kind === 'setup'
    ? `otpauth://totp/Chatty:${encodeURIComponent(username.trim())}?secret=${encodeURIComponent(mfaChallenge.secret)}&issuer=Chatty`
    : null;

  const finishAuthentication = (result) => {
    setMfaChallenge(null);
    setTotpCode('');
    if (result.user.passwordChangeRequired) {
      disableRestrictedSessionRefresh();
      setPasswordRotation({
        username: result.user.username,
        accessToken: result.accessToken,
        isLocalDev: result.accessToken === null,
      });
      setPassword('');
      setNeedsNewPassword(true);
    } else {
      authLogin(result.user.role, result.user.username);
    }
  };

  const enterMfaChallenge = async (challenge) => {
    setNeedsNewPassword(false);
    setPasswordRotation(null);
    setCognitoUser(null);
    setPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setTotpCode('');

    if (challenge.code === 'TOTP_REQUIRED') {
      setMfaChallenge({ kind: 'code', cognitoUser: challenge.cognitoUser });
      return;
    }
    try {
      const secret = await associateTotp(challenge.cognitoUser);
      setMfaChallenge({ kind: 'setup', cognitoUser: challenge.cognitoUser, secret });
    } catch {
      cognitoLogout();
      setMfaChallenge(null);
      setError('setupFailed');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const email = username.trim();
    const normalizedEmail = email.toLowerCase();
    const [localPart, emailDomain, extraPart] = normalizedEmail.split('@');

    if (
      !isMfaDemo && (
        !config.companyEmailDomain ||
        !localPart ||
        !emailDomain ||
        extraPart !== undefined ||
        emailDomain !== config.companyEmailDomain
      )
    ) {
      setError('invalidCredentials');
      return;
    }

    if (!password.trim()) {
      setError('passwordRequired');
      return;
    }

    if (isMfaDemo) {
      if (email.toLowerCase() !== MFA_DEMO_EMAIL || password !== MFA_DEMO_PASSWORD) {
        setError('invalidCredentials');
        return;
      }
      setMfaChallenge(startMfaDemo(email));
      setPassword('');
      return;
    }

    setLoading(true);

    try {
      const result = await login(email, password);
      finishAuthentication(result);
    } catch (err) {
      if (err.code === 'NEW_PASSWORD_REQUIRED') {
        setPasswordRotation(null);
        setCognitoUser(err.cognitoUser);
        setNeedsNewPassword(true);
      } else if (err.code === 'MFA_SETUP_REQUIRED' || err.code === 'TOTP_REQUIRED') {
        await enterMfaChallenge(err);
      } else if (err.code === 'NotAuthorizedException') {
        setError('invalidCredentials');
      } else if (err.code === 'UserNotFoundException') {
        setError('invalidCredentials');
      } else {
        setError('loginFailed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNewPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (passwordRotation && !password.trim()) {
      setError('currentPasswordRequired');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('passwordsDoNotMatch');
      return;
    }
    if (passwordRotation && newPassword === password) {
      setError('newPasswordMustDiffer');
      return;
    }
    if (!meetsNewPasswordPolicy(newPassword)) {
      setError('passwordRequirements');
      return;
    }

    setLoading(true);
    let passwordChanged = false;
    try {
      let result;
      if (passwordRotation) {
        if (passwordRotation.isLocalDev) {
          completeLocalPasswordRotation(passwordRotation.username);
        } else {
          await changeExpiredPassword({
            currentPassword: password,
            newPassword,
            accessToken: passwordRotation.accessToken,
          });
        }
        passwordChanged = true;
        cognitoLogout();
        result = await login(passwordRotation.username, newPassword);
      } else {
        result = await completeNewPassword(cognitoUser, newPassword);
      }
      finishAuthentication(result);
    } catch (err) {
      if (err.code === 'MFA_SETUP_REQUIRED' || err.code === 'TOTP_REQUIRED') {
        await enterMfaChallenge(err);
      } else if (passwordChanged) {
        setPasswordRotation(null);
        setNeedsNewPassword(false);
        setPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setError('passwordChanged');
      } else if (!passwordRotation && err.code === 'NotAuthorizedException') {
        // Cognito challenge sessions are single-use and time-limited; only a fresh login can continue.
        setCognitoUser(null);
        setNeedsNewPassword(false);
        setPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setError('newPasswordSessionExpired');
      } else if (err.code === 'CURRENT_PASSWORD_INCORRECT') {
        setError('incorrectCurrentPassword');
      } else if (err.code === 'PASSWORD_REUSE_NOT_ALLOWED') {
        setError('newPasswordMustDiffer');
      } else if (err.code === 'PASSWORD_HISTORY_REUSE_NOT_ALLOWED') {
        setError('passwordPreviouslyUsed');
      } else if (err.code === 'NEW_PASSWORD_INVALID') {
        setError('invalidNewPassword');
      } else {
        setError('newPasswordFailed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTotpSubmit = async (event) => {
    event.preventDefault();
    if (!mfaChallenge || !/^\d{6}$/.test(totpCode)) return;
    setError('');
    setLoading(true);
    try {
      if (isMfaDemo) {
        if (!await verifyMfaDemoCode(mfaChallenge.secret, totpCode)) {
          throw new Error('Incorrect demo code');
        }
        if (mfaChallenge.kind === 'setup') {
          rememberMfaDemo(username, mfaChallenge.secret);
        }
        setMfaChallenge(null);
        setTotpCode('');
        const user = completeMfaDemoLogin(username.trim());
        authLogin(user.role, user.username);
        return;
      }
      const result = mfaChallenge.kind === 'setup'
        ? await verifyTotpSetup(mfaChallenge.cognitoUser, totpCode)
        : await completeTotpChallenge(mfaChallenge.cognitoUser, totpCode);
      finishAuthentication(result);
    } catch {
      setTotpCode('');
      setError('invalidCode');
    } finally {
      setLoading(false);
    }
  };

  const cancelMfa = () => {
    if (!isMfaDemo) cognitoLogout();
    setMfaChallenge(null);
    setTotpCode('');
    setError('');
  };

  const themeLabel = isDark ? t.switchToLight : t.switchToDark;
  const inputCls =
    'w-full px-4 py-3 rounded-lg bg-transparent border ' +
    'border-light-border placeholder-light-muted disabled:opacity-50 disabled:cursor-not-allowed dark:border-glass dark:placeholder-gray-400';
  const errorCls = 'text-sm text-red-600 dark:text-red-400';
  const submitCls =
    'w-full py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm ' +
    'bg-light-login-btn hover:bg-light-login-btn-hover disabled:hover:bg-light-login-btn ' +
    'text-light-login-btn-text ' +
    'dark:bg-thinking-primary dark:hover:bg-thinking-secondary dark:disabled:hover:bg-thinking-primary dark:text-accent-text';

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-light-bg text-light-text dark:bg-main dark:text-primary">
      <div className="relative w-full max-w-md border rounded-2xl p-8 bg-light-msg border-light-border dark:bg-input dark:border-glass">
        {isMfaDemo && (
          <div className="mb-4 text-sm text-light-muted dark:text-secondary">
            <p className="font-medium">{t.demoNotice}</p>
            <p>{t.demoCredentials} {MFA_DEMO_EMAIL} / {MFA_DEMO_PASSWORD}</p>
          </div>
        )}
        <div className="mb-4 flex items-center justify-end gap-2 text-sm">
          {['en', 'jp'].map((nextLanguage) => (
            <button
              key={nextLanguage}
              type="button"
              onClick={() => onChangeLanguage?.(nextLanguage)}
              aria-pressed={languagePath === nextLanguage}
              className="rounded-lg px-2 py-1 hover:bg-black/5 dark:hover:bg-white/10"
            >
              {nextLanguage === 'en' ? 'English' : '日本語'}
            </button>
          ))}
          <button
            type="button"
            onClick={handleToggleTheme}
            aria-label={themeLabel}
            title={themeLabel}
            className="rounded-lg p-2 transition hover:bg-black/5 dark:hover:bg-white/10"
          >
            {isDark ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </div>
        <h1 className="text-2xl font-bold mb-2">
          {passwordRotation
            ? t.passwordChangeTitle
            : mfaChallenge
            ? mfaChallenge.kind === 'setup' ? t.setupTitle : t.codeTitle
            : t.welcomeTitle}
        </h1>
        <p
          className={
            passwordRotation
              ? 'text-sm mb-6 text-red-600 dark:text-red-400'
              : 'text-sm mb-6 text-light-muted dark:text-secondary'
          }
        >
          {passwordRotation ? (
            <>
              {t.passwordExpiredLine1}
              <br />
              {t.passwordExpiredLine2}
            </>
          ) : needsNewPassword ? (
            t.newPasswordPrompt
          ) : mfaChallenge ? (
            mfaChallenge.kind === 'setup'
              ? t.setupPrompt
              : t.codePrompt
          ) : (
            t.loginPrompt
          )}
        </p>

        {mfaChallenge ? (
          <form onSubmit={handleTotpSubmit} className="space-y-4">
            {mfaChallenge.kind === 'setup' && (
              <div className="space-y-2 text-sm">
                <p>{t.scanQr}</p>
                <TotpQrCode value={totpSetupUri} label={t.qrLabel} />
                <code className="block break-all rounded-lg border border-light-border p-3 text-center select-all dark:border-glass">
                  {mfaChallenge.secret}
                </code>
                <a className="underline" href={totpSetupUri}>{t.openAuthenticator}</a>
                <p>{t.keepSecretPrivate}</p>
              </div>
            )}
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              className={inputCls}
              aria-label={t.codeLabel}
              placeholder={t.codePlaceholder}
              value={totpCode}
              onChange={(event) => setTotpCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
            />
            {error && <p className={errorCls}>{t[error]}</p>}
            <button disabled={loading || totpCode.length !== 6} className={submitCls}>
              {loading ? t.verifying : t.verify}
            </button>
            <button type="button" onClick={cancelMfa} className="w-full text-sm underline">
              {t.backToLogin}
            </button>
            {mfaChallenge.kind === 'code' && (
              <p className="text-center text-xs text-light-muted dark:text-secondary">
                {t.lostAuthenticator}
              </p>
            )}
          </form>
        ) : needsNewPassword ? (
          <form onSubmit={handleNewPassword} className="space-y-4">
            {passwordRotation && (
              <input
                type="password"
                autoComplete="current-password"
                className={inputCls}
                placeholder={t.currentPassword}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            )}
            <div className="relative">
              <input
                type={showNewPassword ? 'text' : 'password'}
                autoComplete="new-password"
                className={`${inputCls} password-visibility-input pr-12`}
                placeholder={t.newPassword}
                value={newPassword}
                onCopy={(event) => event.preventDefault()}
                onChange={(e) => {
                  const nextPassword = e.target.value;
                  setNewPassword(nextPassword);
                  if (!meetsNewPasswordPolicy(nextPassword)) {
                    setConfirmPassword('');
                  }
                }}
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(visible => !visible)}
                className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-light-muted hover:bg-black/5 hover:text-light-text dark:text-secondary dark:hover:bg-white/10 dark:hover:text-primary"
                aria-label={showNewPassword ? t.hideNewPassword : t.showNewPassword}
                title={showNewPassword ? t.hideNewPassword : t.showNewPassword}
              >
                {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            
            {/* 背景のボックスを削除し、リストのみを配置 */}
            <div className="px-2 text-xs">
              <ul className="space-y-2">
                <RequirementItem met={hasMinLength} text={t.minLength} isTyping={isTyping} />
                <RequirementItem met={hasUpperCase} text={t.uppercase} isTyping={isTyping} />
                <RequirementItem met={hasLowerCase} text={t.lowercase} isTyping={isTyping} />
                <RequirementItem met={hasNumber} text={t.number} isTyping={isTyping} />
              </ul>
            </div>

            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                className={`${inputCls} password-visibility-input pr-12`}
                placeholder={t.confirmPassword}
                disabled={isConfirmPasswordDisabled}
                value={confirmPassword}
                onCopy={(event) => event.preventDefault()}
                onPaste={(event) => event.preventDefault()}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(visible => !visible)}
                disabled={isConfirmPasswordDisabled}
                className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-light-muted hover:bg-black/5 hover:text-light-text disabled:cursor-not-allowed disabled:opacity-50 dark:text-secondary dark:hover:bg-white/10 dark:hover:text-primary"
                aria-label={showConfirmPassword ? t.hideConfirmPassword : t.showConfirmPassword}
                title={showConfirmPassword ? t.hideConfirmPassword : t.showConfirmPassword}
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {error && <p className={errorCls}>{t[error]}</p>}
            <button
              disabled={loading || !canSubmitNewPassword}
              className={submitCls}
            >
              {passwordRotation
                ? loading
                  ? t.changing
                  : t.changePassword
                : loading
                ? t.loggingIn
                : t.logIn}
            </button>
          </form>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              className={inputCls}
              placeholder={t.email}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
            <input
              type="password"
              className={inputCls}
              placeholder={t.password}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {error && <p className={errorCls}>{t[error]}</p>}
            <button disabled={loading || !canSubmitLogin} className={submitCls}>
              {loading ? t.loggingIn : t.logIn}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
